"use client";
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import TextField from "../ui/textField";
import { IBanks, TCreditCard, TIbanCard } from "@/interfaces";
import { FlexBetween, FlexBetweenCol } from "@/components";
import {
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { cn } from "@/lib";
import { BoBuildings2 } from "solar-icon-react/bo";
import { useTranslations } from "next-intl";
import Image from "next/image";
import BankImage from "public/bank.webp";
import { useGetBanks } from "@/hooks";
import {
  findBankByCardNumber,
  findBankByIban,
  findBankByKey,
  hexToRgba,
  toEnglishDigits,
} from "@/lib/utils";

interface CreditCardProps {
  formError?: FieldErrors;
  control?: any;
  variant: "credit" | "iban";
  bankName?: string;
  setValue?: UseFormSetValue<any>;
  bankKey: string;
}

const fieldNamesCredit: (keyof TCreditCard)[] = [
  "input1",
  "input2",
  "input3",
  "input4",
];

const fieldNamesIban: (keyof TIbanCard)[] = [
  "input1",
  "input2",
  "input3",
  "input4",
  "input5",
  "input6",
];

const BankCreditCard: FC<CreditCardProps> = ({
  formError,
  control,
  variant,
  setValue,
  bankKey,
}) => {
  const t = useTranslations();
  const { data: bankOptions } = useGetBanks();

  const [bankInfo, setBankInfo] = useState<IBanks | null>(null);

  const isIban = variant === "iban";
  const fieldNames = isIban ? fieldNamesIban : fieldNamesCredit;

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text");
    const digitsOnly = toEnglishDigits(pastedData).replace(/\D/g, "");

    const chunkSize = isIban ? 4 : 4;
    const chunkCount = fieldNames.length;
    const chunks = [];

    for (let i = 0; i < chunkCount; i++) {
      const start = i * chunkSize;
      const chunk = digitsOnly.slice(start, start + chunkSize);
      if (chunk) chunks.push(chunk);
    }

    chunks.forEach((val, idx) => {
      setValue?.(fieldNames[idx], val);
      const el = inputRefs.current[idx];
      if (el) el.value = val;
    });

    // فوکوس روی فیلد بعد از آخرین مقدار واردشده
    const nextIndex =
      chunks.length < fieldNames.length ? chunks.length : fieldNames.length - 1;
    inputRefs.current[nextIndex]?.focus();
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number,
    field: any,
  ) => {
    const value = e.target.value;
    const convertedValue = toEnglishDigits(value);

    // Update the field with converted value
    field.onChange(convertedValue);

    if (convertedValue.length === 4 && index < fieldNames.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleBackspace = (
    e: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = (e.target as HTMLInputElement).value;
    if (e.key === "Backspace" && !value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const values = useWatch({
    control,
    name: fieldNames,
  });

  useEffect(() => {
    const allValues = values?.join("") || "";
    const defaultBank = findBankByKey(bankKey, bankOptions?.data);

    if (!bankInfo && defaultBank) {
      setBankInfo(defaultBank);
    }

    if (allValues.length >= 6) {
      const findBank = isIban ? findBankByIban : findBankByCardNumber;
      const result = findBank(allValues, bankOptions?.data);
      setBankInfo(result || null);
    } else if (allValues.length === 0) {
      setBankInfo(defaultBank || null);
    }
  }, [values, bankOptions, bankKey, isIban, bankInfo]);

  return (
    <FlexBetweenCol
      className={cn("shadow-main w-full rounded-lg bg-white py-4 lg:py-6")}
      style={
        bankInfo
          ? {
              background: `
              radial-gradient(
              circle at 10% 30%,
              ${hexToRgba(bankInfo.color, 0.4)} 0%,
              ${hexToRgba(bankInfo.color, 0.06)} 40%,
              ${hexToRgba(bankInfo.color, 0.12)} 70%,
              ${hexToRgba(bankInfo.color, 0.3)} 100%
            )`,
            }
          : undefined
      }
    >
      <header
        className={cn(
          "mb-10 flex h-7 w-full items-center justify-between overflow-hidden px-4 lg:mb-16 lg:h-10 lg:px-8 xl:mb-20",
          !bankInfo && "bg-gray-200",
        )}
      >
        <FlexBetween gap={2}>
          {bankInfo ? (
            <Image
              src={bankInfo.logo}
              width={32}
              height={32}
              alt={bankInfo.label}
            />
          ) : (
            <BoBuildings2 className="text-primary-main!" />
          )}
          <span
            className={cn(
              `${bankInfo?.color || "text-primary-main"} text-sm lg:text-base`,
            )}
          >
            {bankInfo ? bankInfo.label : t("creditCard.bankName")}
          </span>
        </FlexBetween>
        <Image
          src={BankImage}
          width={32}
          height={32}
          alt={t("creditCard.bankName")}
          title={t("creditCard.bankName")}
        />
      </header>

      <FlexBetween
        gap={2}
        className={cn("flex-row-reverse px-4 lg:px-8 xl:gap-4")}
      >
        {isIban && <p className={cn("text-xs lg:text-lg")}>IR</p>}
        {fieldNames.map((name, index) => (
          <Controller
            key={index}
            name={name as string}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                aria-label={`credit card ${index}`}
                hasShadow
                onChange={(e) => {
                  handleChange(
                    e as ChangeEvent<HTMLInputElement>,
                    index,
                    field,
                  );
                }}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                onKeyDown={(e) => handleBackspace(e, index)}
                maxLength={4}
                numeric
                inputMode="numeric"
                inputClassName={cn(
                  "dir-ltr text-center rounded-md text-xs md:text-base xl:text-lg xl:h-10 p-0 ",
                )}
                error={!!formError?.[name]}
                onPaste={(e) => handlePaste(e, index)}
              />
            )}
          />
        ))}
      </FlexBetween>
    </FlexBetweenCol>
  );
};

export default BankCreditCard;
